import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Printer, RefreshCw, Truck } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import * as fbs from "@/api/fbs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ISSUE_TITLE,
  boxesWord,
  deadlineLeft,
  deadlineUrgent,
  externalNotice,
  ordersWord,
  sgtinRemovable,
  supplyStatusLabel,
  useRetryAllIssues,
} from "@/lib/fbs";
import { RemoveSgtinButton } from "@/pages/fbs/RemoveSgtinButton";
import { PtmoSkippedNote } from "@/pages/fbs/PtmoSkippedNote";
import { ReplaceSgtinButton } from "@/pages/fbs/ReplaceSgtinDialog";
import { DateInput } from "@/components/DateInput";
import { Input } from "@/components/ui/input";
import { useDebounced } from "@/lib/use-debounced";
import { apiErrorText, LoadState, useGoBack } from "@/lib/scan-ui/screen";

/** «Разбор» ПОСТАВКИ — позиции, которые не доехали до конца.
 *
 * ⚠️ Живёт здесь, а не только в скан-экране. Тот показывает «Разбор» ОТКРЫТОЙ
 * поставки: после передачи в доставку поставка с главного экрана уходит, и
 * чинить её стало нечем — при том что именно тогда чинить и приходится
 * (авария 15.09.2026: ЧЗ недоступен, партии не определились, вся смена в
 * «Разборе», а поставка уже уехала).
 */
function IssuesCard({
  supplyId,
  cabinetId,
}: {
  supplyId: string;
  cabinetId: number;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const [progress, setProgress] = React.useState("");
  const issuesQ = useQuery({
    queryKey: ["fbs-issues", supplyId],
    queryFn: () => fbs.getIssues(supplyId, cabinetId),
  });

  const apply = (issues: fbs.Issues) =>
    qc.setQueryData(["fbs-issues", supplyId], issues);

  const retryMut = useMutation({
    mutationFn: (pickId: string) => fbs.retryIssue(pickId, cabinetId),
    onSuccess: apply,
    onError: (e) => toast.error(apiErrorText(e, "Повторить не удалось")),
  });

  const retryAllMut = useRetryAllIssues(supplyId, cabinetId, apply, setProgress);

  const rows = issuesQ.data?.rows ?? [];
  // ⚠️ Ошибку запроса НЕ прячем: пустая карточка неотличима от «чинить
  // нечего», и оператор шёл бы к «Документы 1С», где выписка откажет по
  // причинам, которые экран только что скрыл.
  if (issuesQ.isError) {
    return (
      <Card>
        <CardContent className="space-y-2 p-4 text-sm">
          <p className="font-medium">Разбор не загрузился</p>
          <p className="text-muted-foreground">
            {apiErrorText(issuesQ.error, "Не удалось получить список позиций")}
          </p>
          <Button size="sm" variant="outline" onClick={() => issuesQ.refetch()}>
            Повторить
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (issuesQ.isLoading || rows.length === 0) return null;
  const busy = retryMut.isPending || retryAllMut.isPending;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Разбор · {rows.length}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Эти позиции не доехали до конца. Без партии штука не попадёт в документы
          продажи, поэтому «Документы 1С» выписывают ПОСЛЕ разбора.
        </p>
        <div className="flex items-center gap-2">
          <Button onClick={() => retryAllMut.mutate()} disabled={busy}>
            {retryAllMut.isPending ? "Чиню…" : `Повторить всё (${rows.length})`}
          </Button>
          {progress && <span className="text-xs text-muted-foreground">{progress}</span>}
        </div>
        <ul className="divide-y">
          {rows.map((row) => (
            <li key={row.pick_id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
              <div className="flex-1">
                <div className="font-medium">{row.article}</div>
                <div className="text-xs text-muted-foreground">
                  #{row.wb_order_id} · {ISSUE_TITLE[row.issue] ?? row.issue} · партия{" "}
                  {row.partia_name ?? "—"}
                </div>
                {row.issue === "sgtin_rejected" && row.sgtin_decision_text && (
                  <div className="text-xs text-rose-700">{row.sgtin_decision_text}</div>
                )}
              </div>
              {row.issue === "sgtin_rejected" && (
                <RemoveSgtinButton
                  pickId={row.pick_id}
                  what={`${row.article} · задание #${row.wb_order_id}`}
                  disabled={busy}
                  onRemoved={() => qc.invalidateQueries({ queryKey: ["fbs-issues", supplyId] })}
                />
              )}
              {row.issue === "sgtin_rejected" && (
                <ReplaceSgtinButton
                  row={row}
                  disabled={busy}
                  onReplaced={() => qc.invalidateQueries({ queryKey: ["fbs-issues", supplyId] })}
                />
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => retryMut.mutate(row.pick_id)}
                disabled={busy}
              >
                Повторить
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}


/** Штуки, которые WB не пропустит в доставку: как на наклейке (partA partB),
 *  что за товар, почему. На коробе номера задания нет — ищут по наклейке. */
function SgtinBlockers({
  rows,
  busy,
  onRemoved,
}: {
  rows: fbs.SgtinRow[];
  busy: boolean;
  /** КИЗ убран — перечень надо перечитать: штука из него уходит. */
  onRemoved: () => void;
}): React.ReactElement {
  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-2 text-sm">
      <p className="font-medium">WB не пропустит в доставку · {rows.length} шт.</p>
      <ul className="mt-1 space-y-1">
        {rows.map((r) => {
          const sticker =
            [r.sticker_part_a, r.sticker_part_b].filter(Boolean).join(" ") ||
            `задание ${r.wb_order_id}`;
          return (
            <li key={r.pick_id} className="flex items-center gap-2">
              <span className="flex-1">
                <b>{sticker}</b> —{" "}
                {[r.article, r.nomenclature_name].filter(Boolean).join(" ") || "—"} —{" "}
                {r.decision_text ?? r.decision}
              </span>
              {sgtinRemovable(r) && (
                <RemoveSgtinButton
                  pickId={r.pick_id}
                  what={`${sticker} · ${r.article}`}
                  disabled={busy}
                  onRemoved={onRemoved}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const DOC_LABELS: Record<string, string> = {
  stock_transfer: "Перемещение",
  intercompany: "Передача между организациями",
  invoice: "Счёт-фактура",
  order: "Заказ покупателя",
  expense: "Расходная накладная",
  // УПД к расходной (у профиля с `fbs_expense_upd`). Не путать с `invoice` —
  // счёт-фактурой к передаче между организациями.
  upd: "УПД",
};

function docLabel(kind: string): string {
  return DOC_LABELS[kind] ?? kind;
}

const docList = (docs: { kind: string; number: string }[]): string =>
  docs.map((d) => `${docLabel(d.kind)} ${d.number}`).join(", ");

/** Состояние документов 1С поставки — строкой под списком документов. */
function DocumentsCheckLine({ check }: { check: fbs.DocumentsCheck }): React.ReactElement {
  switch (check.state) {
    case "posted":
      return <p className="text-emerald-700">Документы 1С проведены ✓</p>;
    case "nothing_to_document":
      return (
        <p className="text-muted-foreground">
          Документировать нечего: в поставке нет собранных товаров
        </p>
      );
    case "stale":
      return (
        <p className="text-rose-700">
          В поставке не осталось собранного, а документы 1С есть: {docList(check.stale)}.
          Пометьте их на удаление в 1С и обновите статус
        </p>
      );
    case "missing":
    case "unposted":
      return (
        <div className="space-y-1 text-rose-700">
          {check.unposted.length > 0 && <p>Не проведены: {docList(check.unposted)}</p>}
          {check.missing.length > 0 && (
            <p>Не созданы: {check.missing.map(docLabel).join(", ")}</p>
          )}
        </div>
      );
  }
}

/** Что делает кнопка документов (`POST …/sale-documents`); `null` — кнопки нет.
 *  Старая поставка (`close_requires_posted_docs=false`) — как до проверки:
 *  создать, пока документов нет. Новая — по состоянию проверки: создать,
 *  доделать недостающие или провести; без состояния (грузится, ошибка) — нет. */
function documentsAction(
  closing: fbs.SupplyClosing,
  check: fbs.DocumentsCheck | undefined,
): { label: string; pending: string; disabled?: boolean } | null {
  if (!closing.close_requires_posted_docs) {
    // ⚠️ Кнопка гаснет, КОГДА документы уже есть: повтор идемпотентен, но
    // человек этого не знает и жмёт ещё раз.
    return closing.documents.length > 0
      ? { label: "Документы созданы", pending: "", disabled: true }
      : { label: "Создать документы в 1С", pending: "Создаём документы…" };
  }
  if (check?.state === "unposted") {
    return { label: "Провести документы", pending: "Проводим документы…" };
  }
  if (check?.state !== "missing") return null;
  return closing.documents.length === 0
    ? { label: "Создать документы в 1С", pending: "Создаём документы…" }
    : { label: "Создать недостающие и провести", pending: "Проводим документы…" };
}

/** Почему «Закрыть поставку» неактивна; `null` — можно закрывать.
 *  ⚠️ Закрытая с непроведёнными документами поставка держала товар «в пути»
 *  в синке остатков (NMK-159, 16.10.2026) — то же правило стережёт сервер.
 *  Старую поставку (до проверки) сервер закрывает по-старому — и экран тоже.
 *  Последний запрос экрана или проверки в ошибке — закрывать нельзя: экран
 *  показывает уже не то, что есть. */
function closeBlockReason(
  closing: fbs.SupplyClosing,
  closingFailed: boolean,
  checkQ: { data?: fbs.DocumentsCheck; isError: boolean },
): string | null {
  if (closing.status !== "delivered") {
    return closing.status === "closed"
      ? "Поставка уже закрыта"
      : "Закрыть можно только переданную в доставку поставку — иначе задания в ней останутся без движения";
  }
  if (closingFailed) return "Экран не обновился — закрыть поставку нельзя";
  if (!closing.close_requires_posted_docs) return null;
  if (checkQ.isError) return "Состояние документов 1С не получено — закрыть поставку нельзя";
  const state = checkQ.data?.state;
  if (!state) return "Проверяем документы 1С…";
  if (state === "posted" || state === "nothing_to_document") return null;
  if (state === "stale") return "Сначала пометьте документы 1С на удаление";
  return "Сначала проведите документы 1С";
}

/** Проверка документов 1С поставки под проверкой: состояние, ошибка
 *  запроса — плашкой здесь же (не весь экран), «Обновить статус». */
function DocumentsCheckBlock({
  check,
  error,
  fetching,
  onRefresh,
}: {
  check: fbs.DocumentsCheck | undefined;
  error: unknown;
  fetching: boolean;
  onRefresh: () => void;
}): React.ReactElement {
  return (
    <div className="space-y-2">
      {error ? (
        <p className="whitespace-pre-line rounded border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
          {apiErrorText(error, "Не удалось проверить документы 1С")}
        </p>
      ) : check ? (
        <DocumentsCheckLine check={check} />
      ) : (
        <p className="text-muted-foreground">Проверяем документы 1С…</p>
      )}
      <Button size="sm" variant="outline" onClick={onRefresh} disabled={fetching}>
        <RefreshCw className={`mr-1 size-4 ${fetching ? "animate-spin" : ""}`} />
        Обновить статус
      </Button>
    </div>
  );
}

/** «Отложить» = «не в ЭТУ поставку». Слова «на завтра» нет намеренно: товар
 *  может найтись через десять минут, и задание уедет следующей поставкой. */
const DEFER_HINT =
  "«Отложить» — значит не в эту поставку. Задание остаётся доступным к сбору: " +
  "найдётся товар — отсканируете, и оно уедет следующей поставкой. «Отменить» необратимо.";

/**
 * S4 — завершение ПОСТАВКИ (одно юрлицо). Порядок секций = порядок в жизни:
 * коробá и этикетки → перечитать статусы → разобрать несобранное → передать
 * в доставку → документы 1С → закрыть поставку.
 */
export function FbsFinishPage(): React.ReactElement {
  const { supplyId = "" } = useParams();
  const navigate = useNavigate();
  const goBack = useGoBack("/fbs");
  const qc = useQueryClient();

  const closingQ = useQuery({
    queryKey: ["fbs-closing", supplyId],
    queryFn: () => fbs.getClosing(supplyId),
  });
  const closing = closingQ.data;
  // Проведённость документов — отдельным запросом: он читает 1С, и экран не
  // должен от неё зависеть. Только для поставок под проверкой.
  const guarded = closing?.close_requires_posted_docs === true;
  const checkKey = ["fbs-documents-check", supplyId];
  const checkQ = useQuery({
    queryKey: checkKey,
    queryFn: () => fbs.getDocumentsCheck(supplyId),
    enabled: guarded,
  });

  // Отменённые покупателем приходят один раз, ответом на «Перечитать статусы»,
  // а вынимать их из коробов надо руками. Держим список на клиенте, чтобы он
  // пережил перезагрузку страницы (в `closing` их нет — там только несобранное).

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ["fbs-closing", supplyId] });
    qc.invalidateQueries({ queryKey: checkKey });
    qc.invalidateQueries({ queryKey: ["fbs-overview"] });
  };
  const fail = (e: unknown): void => {
    toast.error(apiErrorText(e));
  };

  // Грузоместа и этикетки заводятся одним заходом в конце — так же, как в
  // интерфейсе WB. Состав грузоместа WB не хранит, поэтому этикетки обезличены
  // и клеятся на любой короб.
  const labelsMut = useMutation({
    mutationFn: (reprint: boolean) => fbs.printBoxLabels(supplyId, reprint),
    onSuccess: (r) => {
      invalidate();
      // ⚠️ «Печатать нечего» теперь значит «все НАПЕЧАТАНЫ», а не «всем
      // заведены грузоместа». Раньше это были одно и то же условие, и после
      // сбоя принтера посреди пачки экран отвечал «все коробá уже с
      // этикетками» на голые коробá — рядом есть кнопка перепечатки.
      if (r.printed.length === 0) toast.info("Все коробá уже напечатаны");
      else toast.success(`Этикетки напечатаны: коробá ${r.printed.join(", ")}`);
      r.warnings.forEach((w) => toast.warning(w, { duration: 10000 }));
    },
    onError: (e) =>
      toast.error(apiErrorText(e, "Не удалось напечатать этикетки"), {
        duration: 20000,
      }),
  });

  const refreshMut = useMutation({
    mutationFn: () => fbs.refreshStatuses(supplyId),
    onSuccess: (r) => {
      invalidate();
      r.errors.forEach((err) => toast.error(err));
      toast.success(
        r.cancelled.length
          ? `Покупатель отменил заданий: ${r.cancelled.length} — выньте их из коробов`
          : "Новых отмен нет",
      );
      const external = externalNotice(r.external);
      if (external) toast.warning(external, { duration: 15000 });
    },
    onError: fail,
  });

  const actionMut = useMutation({
    mutationFn: ({ taskId, action }: { taskId: string; action: "cancel" | "defer" }) =>
      fbs.taskAction(taskId, action, supplyId),
    onSuccess: (data) => {
      qc.setQueryData(["fbs-closing", supplyId], data);
      // Отложенное/отменённое уходит из продажи — состояние документов
      // меняется (например, на «документы есть, продавать нечего»).
      qc.invalidateQueries({ queryKey: checkKey });
    },
    onError: fail,
  });

  const deferAllMut = useMutation({
    mutationFn: () => fbs.deferAll(supplyId),
    onSuccess: (data) => {
      qc.setQueryData(["fbs-closing", supplyId], data);
      qc.invalidateQueries({ queryKey: checkKey });
      toast.success("Несобранное отложено — уедет следующей поставкой");
    },
    onError: fail,
  });

  // ⚠️ Отказ «В доставку» показываем ЦЕЛИКОМ и держим на экране, а не
  // тостом: при непрошедшей маркировке это перечень штук (наклейка — товар —
  // причина), по которому их ищут в коробах, и исчезающий тост его съест.
  const [deliverError, setDeliverError] = React.useState("");
  const deliverMut = useMutation({
    mutationFn: () => fbs.deliverSupply(supplyId),
    onMutate: () => setDeliverError(""),
    onSuccess: (r) => {
      invalidate();
      toast.success(`Передано в доставку: ${r.delivered || "—"}`);
    },
    onError: (e) => {
      setDeliverError(apiErrorText(e, "Передать в доставку не удалось"));
      // Сверка перед доставкой записала статусы КИЗ и могла положить позиции
      // в «Разбор» — перечитываем его.
      qc.invalidateQueries({ queryKey: ["fbs-issues", supplyId] });
    },
  });

  // «Проверить маркировку» — только чтение WB: статусы КИЗ пишутся к
  // позициям, ошибочные уходят в «Разбор», ставшие проходными — оттуда.
  const sgtinMut = useMutation({
    mutationFn: () => fbs.checkSgtin(supplyId),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["fbs-issues", supplyId] });
      if (r.checked === 0) toast.info("КИЗ по этой поставке в WB не отправлялись");
      else if (r.blocking === 0) toast.success(`Маркировка проверена: ${r.checked} шт., всё проходит`);
      else toast.warning(`Не пропустит WB: ${r.blocking} из ${r.checked} шт.`);
    },
    onError: fail,
  });

  // Один и тот же `POST …/sale-documents` и создаёт цепочку, и ДОВОДИТ уже
  // созданную (проводит недопроведённое). После любого исхода состояние
  // документов перечитываем: оно считается в 1С, а не в ответе этой ручки.
  // ⚠️ Отказ — плашкой в разметке, не тостом: это текст 1С о том, чего не
  // хватает (остаток, партия), и по нему чинят, прежде чем жать ещё раз.
  const [docsError, setDocsError] = React.useState("");
  const [closeError, setCloseError] = React.useState("");
  const docsMut = useMutation({
    mutationFn: () => fbs.createSaleDocuments(supplyId),
    onMutate: () => setDocsError(""),
    onSuccess: async (r) => {
      setCloseError("");
      qc.invalidateQueries({ queryKey: ["fbs-overview"] });
      const [freshClosing, freshCheck] = await Promise.all([
        closingQ.refetch(),
        guarded ? checkQ.refetch() : null,
      ]);
      if (r.documents.length) {
        toast.success(`Создано: ${r.documents.map((d) => d.number).join(", ")}`);
        return;
      }
      // ⚠️ Пустой ответ — НЕ «документы уже созданы»: на поставке без
      // собранных товаров это была неправда (документов нет и не будет).
      if (!guarded) {
        // Старая поставка: кнопка есть только без документов, и пустой ответ
        // значит «документировать нечего».
        if (!freshClosing.isError) toast.info("Документировать нечего");
        return;
      }
      // Ошибку перечитывания показывает плашка в блоке документов.
      if (!freshCheck || freshCheck.isError) return;
      const state = freshCheck.data?.state;
      if (state === "nothing_to_document") {
        toast.info("Документировать нечего");
      } else if (state === "posted") {
        toast.success("Документы 1С проведены");
      } else {
        toast.warning("Документы 1С не проведены — см. «Документы 1С»");
      }
    },
    onError: (e) => {
      setDocsError(apiErrorText(e, "Документы 1С не удалось создать или провести"));
      invalidate();
    },
  });

  // ⚠️ 409 закрытия — это «документы 1С не проведены» с перечнем: держим
  // текст сервера на экране, а не в исчезающем тосте. Объявлен выше `docsMut`:
  // успешное «Провести документы» его сбрасывает.
  const closeSupplyMut = useMutation({
    mutationFn: () => fbs.closeSupply(supplyId),
    onMutate: () => setCloseError(""),
    onSuccess: (overview) => {
      qc.setQueryData(["fbs-overview"], overview);
      invalidate();
      toast.success("Поставка закрыта");
      navigate("/fbs", { replace: true });
    },
    onError: (e) => {
      setCloseError(apiErrorText(e, "Закрыть поставку не удалось"));
      invalidate();
    },
  });

  // Новое успешное чтение проверки перевело её в «можно закрывать» — отказ
  // закрытия, висящий на экране, уже не про то, что есть. Только ПЕРЕХОД:
  // перечитывание сразу после отказа с тем же «проведены» (замок занят)
  // стёрло бы текст «повторите», не дав его прочесть.
  const checkState = checkQ.isSuccess ? checkQ.data.state : undefined;
  const prevCheckState = React.useRef<fbs.DocumentsCheckState | undefined>(undefined);
  React.useEffect(() => {
    if (!checkState) return;
    const closable = (st: fbs.DocumentsCheckState | undefined): boolean =>
      st === "posted" || st === "nothing_to_document";
    if (closable(checkState) && prevCheckState.current && !closable(prevCheckState.current)) {
      setCloseError("");
    }
    prevCheckState.current = checkState;
  }, [checkState, checkQ.dataUpdatedAt]);

  const zeroOrders = closing?.unpicked.filter((t) => t.is_zero_order) ?? [];
  const checkData = guarded && !checkQ.isError ? checkQ.data : undefined;
  const docsAction = closing ? documentsAction(closing, checkData) : null;
  const closeBlock = closing ? closeBlockReason(closing, closingQ.isError, checkQ) : null;
  const busy = actionMut.isPending || deferAllMut.isPending;

  return (
    <div className="space-y-4">
      {/* Шапка — всегда, до и вместо данных: иначе при ошибке загрузки экран
          остаётся без единственной кнопки выхода. */}
      <div className="flex items-center gap-2">
        <button type="button" onClick={goBack} aria-label="Назад">
          <ArrowLeft className="size-5" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold">Завершение поставки</h1>
          {closing && (
            <p className="text-sm text-muted-foreground">
              {closing.cabinet_name} · {closing.wb_supply_id} ·{" "}
              {supplyStatusLabel(closing.status)}
            </p>
          )}
        </div>
      </div>

      {!closing ? (
        <LoadState
          isLoading={closingQ.isLoading}
          error={closingQ.error}
          onRetry={() => closingQ.refetch()}
          className="flex flex-col items-center justify-center gap-3 rounded-xl border p-6 text-center"
        />
      ) : (
        <>
          {/* Повторная загрузка не удалась, а данные прежние — говорим об
              этом, иначе экран выдаёт старое за текущее. */}
          {closingQ.isError && (
            <div className="space-y-2 rounded border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
              <p>{apiErrorText(closingQ.error, "Экран не обновился")}</p>
              <Button size="sm" variant="outline" onClick={() => closingQ.refetch()}>
                Повторить
              </Button>
            </div>
          )}
          {closing.cancelled.length > 0 && (
            <Card className="border-rose-300">
              <CardHeader>
                <CardTitle className="text-lg text-rose-700">
                  <AlertTriangle className="mr-1 inline size-5" />
                  Отменено покупателем — вынуть из коробов
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                {closing.cancelled.map((t) => (
                  <div key={t.task_id} className="rounded border border-rose-200 p-2">
                    {/* Крупно — номер СТИКЕРА: на коробке напечатан он, а не
                        номер задания, и искать штуку кладовщик будет по нему. */}
                    {t.sticker_part_a || t.sticker_part_b ? (
                      <p className="font-mono text-lg font-semibold">
                        {t.sticker_part_a} {t.sticker_part_b}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        номер стикера не сохранён — ищите по заданию
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {t.article}
                      {t.box_position != null && ` · короб №${t.box_position}`}
                      {` · задание ${t.wb_order_id}`}
                    </p>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">
                  WB уже вынул эти задания из поставки — товар со стикером остался в коробе и
                  уедет «мимо заказа». Список приходит с сервера, перезагрузка его не потеряет.
                </p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                Коробá — {closing.boxes_total} {boxesWord(closing.boxes_total)}
                {closing.boxes_open > 0 && (
                  <span className="ml-2 text-sm font-normal text-amber-700">
                    не закрыто: {closing.boxes_open}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="rounded-lg border p-2">
                <div className="mt-1 text-xs text-muted-foreground">
                  {closing.supply.boxes
                    .map(
                      (b) =>
                        `№${b.position} (${b.orders} ${ordersWord(b.orders)})` +
                        (b.wb_trbx_id ? "" : " — без этикетки"),
                    )
                    .join(", ") || "коробов нет"}
                </div>
              </div>
              <div className="rounded-lg border border-dashed p-2">
                <p className="mb-2 text-xs text-muted-foreground">
                  Грузоместа заводятся в WB и печатаются одним заходом, когда всё собрано —
                  так же, как в интерфейсе WB. Этикетки обезличены: состав короба площадка не
                  хранит, поэтому клеить их можно в любом порядке. Без этикетки короб в ПВЗ
                  не примут.
                </p>
                <Button
                  size="sm"
                  onClick={() => labelsMut.mutate(false)}
                  disabled={labelsMut.isPending || closing.boxes_total === 0}
                >
                  <Printer className="mr-1 size-4" />
                  Напечатать этикетки коробов
                </Button>
                {/* Перепечатка рядом, а не вместо: принтер отваливается
                    посреди пачки, этикетки рвутся и заливаются, и «уже
                    печатали» не должно быть тупиком. */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => labelsMut.mutate(true)}
                  disabled={labelsMut.isPending || closing.boxes_total === 0}
                >
                  Перепечатать все
                </Button>
              </div>
              {closing.carryover_supplies.length > 0 && (
                <p className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-700">
                  Поставка-парковка: {closing.carryover_supplies.join(", ")} — в доставку она не
                  передаётся, отложенные задания остаются доступными к сбору.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Не собрано — {closing.unpicked.length}</CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={() => refreshMut.mutate()}
                disabled={refreshMut.isPending}
              >
                <RefreshCw
                  className={`mr-1 size-4 ${refreshMut.isPending ? "animate-spin" : ""}`}
                />
                Перечитать статусы
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {closing.unpicked.length === 0 && (
                <p className="text-sm text-muted-foreground">Всё собрано.</p>
              )}
              {closing.unpicked.length > 0 && (
                <>
                  <p className="text-xs text-muted-foreground">{DEFER_HINT}</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => deferAllMut.mutate()}
                    disabled={busy}
                  >
                    Отложить всё
                  </Button>
                </>
              )}
              {zeroOrders.length > 0 && (
                <p className="rounded bg-emerald-50 px-2 py-1 text-xs text-emerald-800">
                  {zeroOrders.length} задани(е/я) на нулевой остаток — их отмена без штрафа.
                </p>
              )}
              {closing.unpicked.map((task) => (
                <div
                  key={task.task_id}
                  className="flex flex-col gap-2 rounded-lg border p-2 text-sm sm:flex-row sm:items-center"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">
                      {task.article}
                      {task.is_zero_order && (
                        <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800">
                          без штрафа
                        </span>
                      )}
                      {task.status === "deferred" && (
                        <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
                          отложено
                        </span>
                      )}
                    </div>
                    <div
                      className={`text-xs ${
                        deadlineUrgent(task.deadline_at)
                          ? "text-rose-600"
                          : "text-muted-foreground"
                      }`}
                    >
                      #{task.wb_order_id} · {task.cabinet_name} · дедлайн{" "}
                      {deadlineLeft(task.deadline_at)}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 sm:flex-none"
                      onClick={() => actionMut.mutate({ taskId: task.task_id, action: "defer" })}
                      disabled={busy || task.status === "deferred"}
                    >
                      Отложить
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-1 text-destructive sm:flex-none"
                      onClick={() => actionMut.mutate({ taskId: task.task_id, action: "cancel" })}
                      disabled={busy}
                    >
                      Отменить
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Передача в доставку</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-rose-700">
                После передачи поставку изменить нельзя. Сначала закройте коробá, напечатайте
                этикетки и разберите несобранное.
              </p>
              {closing.status === "open" && (
                <ShippingParams
                  supplyId={supplyId}
                  cabinetId={closing.cabinet_id}
                  saved={closing.shipping}
                  disabled={deliverMut.isPending}
                />
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => deliverMut.mutate()}
                  disabled={
                    deliverMut.isPending || closing.boxes_open > 0 || closing.status !== "open"
                  }
                >
                  <Truck className="mr-1 size-4" />
                  {closing.status === "open" ? "Передать в доставку" : "Уже передана"}
                </Button>
                {closing.status === "open" && (
                  <Button
                    variant="outline"
                    onClick={() => sgtinMut.mutate()}
                    disabled={sgtinMut.isPending || deliverMut.isPending}
                  >
                    {sgtinMut.isPending ? "Проверяю…" : "Проверить маркировку"}
                  </Button>
                )}
              </div>
              {sgtinMut.data && sgtinMut.data.blocking > 0 && (
                <SgtinBlockers
                  rows={sgtinMut.data.rows.filter((r) => r.blocking)}
                  busy={sgtinMut.isPending || deliverMut.isPending}
                  // Перечитываем статусы у WB: убранная штука уходит из
                  // перечня, а «Разбор» обновит сам `sgtinMut`.
                  onRemoved={() => sgtinMut.mutate()}
                />
              )}
              {deliverError && (
                <p className="whitespace-pre-line rounded border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
                  {deliverError}
                </p>
              )}
            </CardContent>
          </Card>

          <IssuesCard supplyId={supplyId} cabinetId={closing.cabinet_id} />

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Документы 1С</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {closing.documents.length === 0 && (
                <p className="text-muted-foreground">Документов пока нет.</p>
              )}
              <PtmoSkippedNote skipped={closing.ptmo_skipped} />
              <ul className="space-y-1">
                {closing.documents.map((d) => (
                  <li key={`w-${d.kind}-${d.number}`}>
                    {/* Состояние проведения — в проверке ниже (`…/documents-check`), а не
                        подписью у строки: этот список его не несёт. */}
                    {docLabel(d.kind)}: <b>{d.number}</b>
                  </li>
                ))}
              </ul>
              {guarded && (
                <DocumentsCheckBlock
                  check={checkQ.data}
                  error={checkQ.isError ? checkQ.error : null}
                  fetching={checkQ.isFetching}
                  onRefresh={() => checkQ.refetch()}
                />
              )}
              {docsAction && (
                <Button
                  onClick={() => docsMut.mutate()}
                  disabled={docsMut.isPending || docsAction.disabled}
                >
                  {docsMut.isPending ? docsAction.pending : docsAction.label}
                </Button>
              )}
              {docsError && (
                <p className="whitespace-pre-line rounded border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
                  {docsError}
                </p>
              )}
            </CardContent>
          </Card>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={() => closeSupplyMut.mutate()}
                disabled={closeSupplyMut.isPending || closeBlock !== null}
                title={closeBlock ?? ""}
              >
                {closing.status === "closed" ? "Поставка закрыта" : "Закрыть поставку"}
              </Button>
              {closeBlock && closing.status === "delivered" && (
                <span className="text-sm text-rose-700">{closeBlock}</span>
              )}
            </div>
            {closeError && (
              <p className="whitespace-pre-line rounded border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
                {closeError}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Параметры отгрузки поставки — способ, пункт и дата.
 *
 * ⚠️ WB требует их, чтобы ЗАКРЫТЬ поставку: отказ `SupplyShippingRequired` —
 * «Specify shipping point, type and date to close the supply». Пока портал их
 * не умел, оператор второй инсталляции проставлял это руками в кабинете WB.
 *
 * ⚠️ Требует НЕ У ВСЕХ, и признака в ответе WB нет: наши поставки закрывались с
 * пустыми полями, у второй — заполнены руками. Поэтому блок необязательный и
 * свёрнут: кто без него обходится, тот его и не открывает.
 */
function ShippingParams({
  supplyId,
  cabinetId,
  saved,
  disabled,
}: {
  supplyId: string;
  cabinetId: number;
  saved: fbs.SupplyShipping;
  disabled?: boolean;
}): React.ReactElement {
  const filled = saved.shipping_point_id !== null;
  const [open, setOpen] = React.useState(false);
  const [type, setType] = React.useState<string>(saved.shipping_type ?? fbs.SHIPPING_SELF);
  const [pointId, setPointId] = React.useState<number | null>(saved.shipping_point_id);
  const [date, setDate] = React.useState(saved.shipping_date ?? "");
  const [waybill, setWaybill] = React.useState(saved.waybill_uuid ?? "");
  // ⚠️ Город и тип товара — обязательные фильтры WB, а не удобство: без них он
  // не отдаёт пунктов вовсе. Города портал не подставляет — его называет тот,
  // кто повезёт поставку.
  const [city, setCity] = React.useState("");
  const [cargoType, setCargoType] = React.useState<number | null>(null);
  // ⚠️ Дебаунс: город идёт прямо в ключ запроса, и без него «Москва» — это
  // ПЯТЬ обращений в WB подряд, каждое под его же 429-бэкоффом.
  const debouncedCity = useDebounced(city.trim(), 300);
  const qc = useQueryClient();

  const cargoTypes = useQuery({
    queryKey: ["fbs-config"],
    queryFn: fbs.getConfig,
    staleTime: 5 * 60_000,
  });
  const canSearch = debouncedCity.length > 1 && cargoType !== null;
  const points = useQuery({
    // ⚠️ Ключ по КАБИНЕТУ, а не по поставке: список пунктов зависит от токена
    // WB, города и типа груза — от поставки он не зависит вовсе, и ключ по ней
    // заставлял бы каждую следующую поставку юрлица тянуть тот же список.
    queryKey: ["fbs-shipping-points", cabinetId, debouncedCity, cargoType],
    queryFn: () =>
      fbs.getShippingPoints(supplyId, { city: debouncedCity, cargo_type: cargoType ?? 0 }),
    enabled: open && canSearch,
    staleTime: 5 * 60_000,
  });

  const save = useMutation({
    mutationFn: () => {
      if (pointId === null) throw new Error("Не выбран пункт отгрузки");
      return fbs.setSupplyShipping(supplyId, {
        shipping_type: type,
        shipping_point_id: pointId,
        shipping_date: date,
        waybill_uuid: waybill.trim() || null,
      });
    },
    onSuccess: () => {
      toast.success("Параметры отгрузки сохранены");
      qc.invalidateQueries({ queryKey: ["fbs-closing", supplyId] });
    },
    onError: (e) => toast.error(apiErrorText(e, "Не удалось сохранить")),
  });

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} disabled={disabled}>
        {filled ? "Параметры отгрузки заданы" : "Параметры отгрузки"}
      </Button>
    );
  }
  // ⚠️ ЭТрН обязательна только у транспортной компании — WB иначе не примет
  // поставку. Проверяем здесь и на бэке: экран обходится, последствие тихое.
  const needWaybill = type === fbs.SHIPPING_COMPANY && !waybill.trim();
  // Дата: `DateInput` сам отдаёт пустую строку на неполном и неверном вводе,
  // поэтому второй разбор здесь был бы копией его же правила.
  const ready = pointId !== null && date !== "" && !needWaybill;

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="text-sm font-medium">Параметры отгрузки</div>

      <div className="flex flex-wrap gap-2">
        {[
          { v: fbs.SHIPPING_SELF, t: "Везём сами" },
          { v: fbs.SHIPPING_COMPANY, t: "Транспортной компанией" },
        ].map((o) => (
          <Button
            key={o.v}
            size="sm"
            variant={type === o.v ? "default" : "outline"}
            onClick={() => setType(o.v)}
          >
            {o.t}
          </Button>
        ))}
      </div>

      <Input
        value={city}
        placeholder="Населённый пункт отгрузки (например, Москва)"
        onChange={(e) => setCity(e.target.value)}
      />
      <select
        className="h-10 w-full rounded-md border px-2 text-sm"
        value={cargoType ?? ""}
        onChange={(e) => setCargoType(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">Тип товара</option>
        {(cargoTypes.data?.cargo_types ?? []).map((t) => (
          <option key={t.value} value={t.value}>
            {t.title}
          </option>
        ))}
      </select>

      {points.isError && (
        <p className="text-xs text-rose-700">
          {apiErrorText(points.error, "WB не отдал список пунктов отгрузки")}
        </p>
      )}
      <select
        className="h-10 w-full rounded-md border px-2 text-sm"
        value={pointId ?? ""}
        onChange={(e) => setPointId(e.target.value ? Number(e.target.value) : null)}
        disabled={!canSearch || points.isLoading}
      >
        <option value="">
          {!canSearch
            ? "Укажите город и тип товара"
            : points.isLoading
              ? "Загружаем пункты отгрузки…"
              : "Выберите пункт отгрузки"}
        </option>
        {(points.data ?? []).map((p) => (
          <option key={p.id} value={p.id}>
            {[p.name, p.city, p.address].filter(Boolean).join(" · ")}
          </option>
        ))}
      </select>
      {canSearch && points.isSuccess && (points.data ?? []).length === 0 && (
        <p className="text-xs text-amber-700">
          В «{debouncedCity}» пунктов для этого типа товара WB не показал.
        </p>
      )}

      <DateInput value={date} onChange={(iso) => setDate(iso)} ariaLabel="дата отгрузки" />

      {type === fbs.SHIPPING_COMPANY && (
        <div>
          <Input
            value={waybill}
            placeholder="ID ЭТрН (обязательно)"
            onChange={(e) => setWaybill(e.target.value)}
          />
          <p className="mt-0.5 text-xs text-muted-foreground">
            Оформляется через ЭДО. Без него WB не примет поставку.
          </p>
        </div>
      )}

      <Button size="sm" onClick={() => save.mutate()} disabled={!ready || save.isPending}>
        Сохранить
      </Button>
    </div>
  );
}
